// src/pages/onboarding/start.tsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Switch } from "@/components/ui/switch";
import {
  ExternalLink,
  Copy,
  CheckCircle,
  Clock,
  Shield,
  Bug,
} from "lucide-react";
import { getGoogleAdsScript } from "@/lib/google-ads-script-template";
import { cn } from "@/lib/utils";
import { useOnboarding } from "@/contexts/onboarding-context";
import { DEV_FALLBACK_WEB_APP_URL } from "@/lib/onboarding-data-utils";
import { Link } from "react-router-dom";

const TEMPLATE_URL =
  "https://docs.google.com/spreadsheets/d/1XmQYHWYbQbtn6mnrGYB71dsixbp66Eo3n5o7fka_s10/copy";
const DEBUG = process.env.NODE_ENV === "development";

export default function OnboardingStartPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setStage } = useOnboarding();

  const [currentStep, setCurrentStep] = useState(1);
  const [sheetUrl, setSheetUrl] = useState("");
  const [localWebAppUrl, setLocalWebAppUrl] = useState("");
  const [scriptCopied, setScriptCopied] = useState(false);
  const [isDebugMode, setIsDebugMode] = useState(false);

  // For developer convenience, this will skip the steps and pre-fill the data when debug mode is enabled
  useEffect(() => {
    if (DEBUG && isDebugMode) {
      setSheetUrl(
        "https://docs.google.com/spreadsheets/d/DEV_MODE_ENABLED/edit",
      );
      setScriptCopied(true);
      setLocalWebAppUrl(DEV_FALLBACK_WEB_APP_URL);
      setCurrentStep(3);
    } else if (DEBUG && !isDebugMode) {
      // Reset to normal mode
      setSheetUrl("");
      setScriptCopied(false);
      setLocalWebAppUrl("");
      setCurrentStep(1);
    }
  }, [isDebugMode]);

  const copyScript = async () => {
    try {
      const script = getGoogleAdsScript(sheetUrl);
      await navigator.clipboard.writeText(script);
      setScriptCopied(true);
      toast({ title: "Script Copied" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to copy script",
      });
    }
  };

  const copyWebAppUrl = async () => {
    try {
      await navigator.clipboard.writeText(
        "https://script.google.com/macros/s/AKfycbxQT-RpHmMDuGEj2v1XA3_DQIH0diSNcbKISWRqZNef7VJi6LKEb2WvQZSeJ_00caTI9w/exec",
      );
      toast({ title: "Web App URL Copied" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to copy Web App URL",
      });
    }
  };

  const handleWebAppUrlSubmit = async () => {
    if (!localWebAppUrl.includes("script.google.com/macros/s/")) {
      toast({
        variant: "destructive",
        title: "Invalid URL",
        description: "Please provide a valid Google Apps Script Web App URL.",
      });
      return;
    }

    // Store webAppUrl in localStorage for potential transfer later
    localStorage.setItem("temp_web_app_url", localWebAppUrl);
    console.log(
      "[ONBOARDING] Stored webAppUrl in localStorage for transfer:",
      localWebAppUrl,
    );

    setStage("ANALYSIS");
    navigate("/onboarding/analysis", {
      state: {
        webAppUrl: localWebAppUrl,
        debugMode: isDebugMode,
      },
    });
  };

  const steps = [
    {
      number: 1,
      title: "Create Your Sheet",
      description:
        "First, create a copy of the template Google Sheet. Make sure 'Anyone with link' can edit it.",
      content: (
        <div className="space-y-4">
          <Button
            onClick={() => window.open(TEMPLATE_URL, "_blank")}
            className="w-full sm:w-auto"
            size="lg"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Create Copy
          </Button>
          <Input
            value={sheetUrl}
            onChange={(e) => setSheetUrl(e.target.value)}
            placeholder="Paste your sheet URL here"
            className="w-full"
          />
          <Button
            onClick={() => {
              // Store sheet URL in localStorage for potential transfer later
              localStorage.setItem("temp_sheet_url", sheetUrl);
              console.log(
                "[ONBOARDING] Stored sheetUrl in localStorage for transfer:",
                sheetUrl,
              );
              setCurrentStep(2);
            }}
            disabled={!sheetUrl.includes("sheet")}
            className="w-full sm:w-auto"
            size="lg"
          >
            Continue
          </Button>
        </div>
      ),
    },
    {
      number: 2,
      title: "Install the Script",
      description:
        "The script below is now customized with your sheet URL. Copy it, paste it into your Google Ads account, authorize it, and run it once. This should only take about 30 seconds or so. Once you see the data appear in your sheet, then schedule your script to run hourly and move on to step three.",
      content: (
        <div className="space-y-4">
          <Button
            onClick={copyScript}
            variant={scriptCopied ? "secondary" : "default"}
            className="w-full sm:w-auto"
            size="lg"
          >
            <Copy className="mr-2 h-4 w-4" />
            {scriptCopied ? "Copied!" : "Copy Script"}
          </Button>
          <Button
            onClick={() => setCurrentStep(3)}
            disabled={!scriptCopied}
            className="w-full sm:w-auto ml-0 sm:ml-2"
            size="lg"
          >
            Continue
          </Button>
        </div>
      ),
    },
    {
      number: 3,
      title: "Connect Your Account",
      description:
        'After running the script, deploy it as a Web App to get your final URL. Make sure it\'s set to be accessible by "Anyone".',
      content: (
        <div className="space-y-4">
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>To get your Web App URL:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Open your Google Ads sheet</li>
              <li>Click 'Extensions' then 'Apps Script'</li>
              <li>Click "Deploy" and select "New deployment"</li>
              <li>Click the gear icon & select "Web app"</li>
              <li>Add a description, eg '8020agent'</li>
              <li>Set "Execute as" to your account (the one that says 'me')</li>
              <li>Under "Who has access" select "Anyone"</li>
              <li>Click "Deploy" and then Authorise (click allow)</li>
              <li>
                Copy the Web App URL, not the ID. The URL is the one that starts
                script.google.com/...
              </li>
              <li>
                (Note: If you close the window by mistake, click Deploy, then
                Manage Deployments)
              </li>
            </ol>
          </div>
          <Input
            value={localWebAppUrl}
            onChange={(e) => setLocalWebAppUrl(e.target.value)}
            placeholder="Paste your Web App URL here"
            className="w-full"
          />
          <Button
            onClick={handleWebAppUrlSubmit}
            disabled={!localWebAppUrl.includes("script.google.com/macros/s/")}
            className="w-full sm:w-auto"
            size="lg"
          >
            Start Analysis
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-hero relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float" />
        <div
          className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-0 w-64 h-64 bg-success/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "2s" }}
        />
      </div>

      <div className="relative z-10 px-6 py-16">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Let's Get Your Data Connected
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Follow these simple steps to get your personalized report.
            </p>
          </div>

          {/* Trust Indicators */}
          <div
            className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground mb-12 animate-fade-in"
            style={{ animationDelay: "200ms" }}
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-success" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-success" />
              <span>Takes less than 4 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-success" />
              <span>Your data stays secure</span>
            </div>
          </div>

          {/* Main Content - Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Column - Setup Steps */}
            <Card
              className="bg-gradient-card backdrop-blur-sm border border-border/50 shadow-large animate-scale-in"
              style={{ animationDelay: "300ms" }}
            >
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Setup Your Account</CardTitle>
                <CardDescription className="text-lg">
                  Connect your Google Ads data to get started
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {steps.map((step) => (
                  <div
                    key={step.number}
                    className={cn(
                      "relative p-6 rounded-2xl border transition-all duration-300",
                      step.number === currentStep
                        ? "border-primary/50 bg-primary/5 shadow-soft"
                        : step.number < currentStep
                          ? "border-success/50 bg-success/5"
                          : "border-border/50 bg-muted/20 opacity-60",
                    )}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={cn(
                          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 font-semibold",
                          step.number === currentStep
                            ? "bg-primary text-primary-foreground border-primary"
                            : step.number < currentStep
                              ? "bg-success text-success-foreground border-success"
                              : "bg-muted text-muted-foreground border-muted-foreground/30",
                        )}
                      >
                        {step.number < currentStep ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          step.number
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold mb-2">
                          {step.title}
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          {step.description}
                        </p>
                        {step.number === currentStep && (
                          <div className="animate-fade-in">{step.content}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Right Column - Video Tutorial */}
            <div
              className="animate-scale-in"
              style={{ animationDelay: "400ms" }}
            >
              <Card className="bg-gradient-card backdrop-blur-sm border border-border/50 shadow-large">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl">Video Walkthrough</CardTitle>
                  <CardDescription className="text-lg">
                    Watch me complete the setup process step-by-step
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div
                    className="relative w-full"
                    style={{ paddingBottom: "56.25%" }}
                  >
                    <iframe
                      src="https://www.loom.com/embed/1a1a17c5543b4da39ed7d29ce37c4187?sid=8e5c3f4a-f8c7-4c3a-9c7a-2e1f5d6b8a9c"
                      frameBorder="0"
                      allowFullScreen
                      className="absolute top-0 left-0 w-full h-full rounded-lg"
                      title="Setup Walkthrough Video"
                    ></iframe>
                  </div>
                  <div className="mt-4 text-center">
                    <p className="text-sm text-muted-foreground">
                      Follow along with this short tutorial to get set up
                      quickly
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Debug Panel - Only visible in development */}
      {DEBUG && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <Card className="bg-gradient-card backdrop-blur-sm border border-border/50 shadow-large">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Bug className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Debug Panel</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isDebugMode}
                    onCheckedChange={setIsDebugMode}
                    id="debug-mode"
                  />
                  <label htmlFor="debug-mode" className="text-sm">
                    Debug Mode
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={copyWebAppUrl}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy Web App URL
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sign-in Footer */}
      <div
        className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40 animate-fade-in"
        style={{ animationDelay: "800ms" }}
      >
        <p className="text-sm text-muted-foreground bg-background/80 backdrop-blur-sm rounded-lg px-4 py-2 border border-border/50">
          Already have an account?{" "}
          <Link
            to="/sign-in"
            className="text-primary hover:text-primary-dark underline underline-offset-2 transition-colors"
          >
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
